import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Wrench,
  DollarSign,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  Download,
  FileText,
  Plus,
  TrendingUp,
  Package,
  User,
  Building,
  RefreshCw,
  Filter,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Edit3,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyLogForm = {
  equipment_id: '',
  equipment_name: '',
  performed_by: '',
  performed_at: new Date().toISOString().split('T')[0],
  maintenance_type: 'Routine',
  labor_cost: '',
  parts_cost: '',
  contractor_vendor: '',
  notes: '',
  issues_found: '',
  time_spent_minutes: '',
  next_maintenance_date: '',
};

export function MaintenanceDashboard({ isOpen, onClose, currentTour, initialTab = 'overview' }) {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [equipment, setEquipment] = useState([]);

  // Update activeTab when initialTab changes (when opened from overlay)
  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);
  const [isAddLogOpen, setIsAddLogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingLogId, setEditingLogId] = useState(null);
  const [newLog, setNewLog] = useState(emptyLogForm);

  const fetchStats = useCallback(async () => {
    if (!token || !currentTour?.id) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/maintenance-procedures/dashboard/stats?tour_id=${currentTour.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  }, [token, currentTour?.id]);

  const fetchEquipment = useCallback(async () => {
    if (!token || !currentTour?.id) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/equipment/tour/${currentTour.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setEquipment(data);
        console.log('Fetched equipment:', data.length, 'items');
      }
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
    }
  }, [token, currentTour?.id]);

  useEffect(() => {
    if (isOpen) {
      fetchStats();
      fetchEquipment();
    }
  }, [isOpen, fetchStats, fetchEquipment]);

  const handleExportCSV = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/maintenance-procedures/export/csv?tour_id=${currentTour?.id || ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `maintenance_records_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to export CSV:', error);
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/maintenance-procedures/export/pdf?tour_id=${currentTour?.id || ''}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `maintenance_report_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to export PDF:', error);
    }
  };

  const handleAddLog = async () => {
    // Validate - need either equipment selection or manual name, plus performed_by
    const hasEquipment = newLog.equipment_id || newLog.equipment_name;
    if (!hasEquipment || !newLog.performed_by) return;
    
    const selectedEquipment = equipment.find(e => e.id === newLog.equipment_id);
    const equipmentId = newLog.equipment_id === 'manual' ? null : newLog.equipment_id;
    const equipmentName = selectedEquipment?.name || newLog.equipment_name;
    
    const logPayload = {
      tour_id: currentTour.id,
      equipment_id: equipmentId,
      equipment_name: equipmentName,
      performed_by: newLog.performed_by,
      performed_at: newLog.performed_at ? new Date(newLog.performed_at).toISOString() : null,
      maintenance_type: newLog.maintenance_type,
      labor_cost: newLog.labor_cost ? parseFloat(newLog.labor_cost) : null,
      parts_cost: newLog.parts_cost ? parseFloat(newLog.parts_cost) : null,
      contractor_vendor: newLog.contractor_vendor || null,
      notes: newLog.notes || null,
      issues_found: newLog.issues_found || null,
      time_spent_minutes: newLog.time_spent_minutes ? parseInt(newLog.time_spent_minutes) : null,
      next_maintenance_date: newLog.next_maintenance_date ? new Date(newLog.next_maintenance_date).toISOString() : null,
    };
    
    try {
      const url = isEditMode 
        ? `${API_URL}/api/maintenance-procedures/logs/${editingLogId}`
        : `${API_URL}/api/maintenance-procedures/logs`;
      
      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(logPayload),
      });
      
      if (response.ok) {
        closeLogDialog();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to save maintenance log:', error);
    }
  };

  const openEditDialog = (log) => {
    setIsEditMode(true);
    setEditingLogId(log.id);
    setNewLog({
      equipment_id: log.equipment_id || '',
      equipment_name: log.equipment_name || '',
      performed_by: log.performed_by || '',
      performed_at: log.performed_at ? log.performed_at.split('T')[0] : new Date().toISOString().split('T')[0],
      maintenance_type: log.maintenance_type || 'Routine',
      labor_cost: log.labor_cost?.toString() || '',
      parts_cost: log.parts_cost?.toString() || '',
      contractor_vendor: log.contractor_vendor || '',
      notes: log.notes || '',
      issues_found: log.issues_found || '',
      time_spent_minutes: log.time_spent_minutes?.toString() || '',
      next_maintenance_date: log.next_maintenance_date ? log.next_maintenance_date.split('T')[0] : '',
    });
    setIsAddLogOpen(true);
  };

  const closeLogDialog = () => {
    setIsAddLogOpen(false);
    setIsEditMode(false);
    setEditingLogId(null);
    setNewLog(emptyLogForm);
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this maintenance record?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/maintenance-procedures/logs/${logId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to delete maintenance log:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-6 w-6 text-emerald-500" />
                <DialogTitle className="text-xl">Maintenance Dashboard</DialogTitle>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleExportCSV}>
                  <Download className="h-4 w-4 mr-1" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportPDF}>
                  <FileText className="h-4 w-4 mr-1" />
                  PDF
                </Button>
                <Button size="sm" onClick={() => setIsAddLogOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Log Maintenance
                </Button>
              </div>
            </div>
            <DialogDescription>
              Track maintenance costs, schedules, and history for {currentTour?.name}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
                <TabsTrigger value="costs">Costs</TabsTrigger>
              </TabsList>

              {/* OVERVIEW TAB */}
              <TabsContent value="overview" className="space-y-4 mt-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Total Records</p>
                          <p className="text-2xl font-bold">{stats?.summary?.total_maintenance_count || 0}</p>
                        </div>
                        <Wrench className="h-8 w-8 text-blue-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Total Cost</p>
                          <p className="text-2xl font-bold">{formatCurrency(stats?.summary?.total_cost)}</p>
                        </div>
                        <DollarSign className="h-8 w-8 text-emerald-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Upcoming</p>
                          <p className="text-2xl font-bold text-blue-500">{stats?.summary?.upcoming_count || 0}</p>
                        </div>
                        <Calendar className="h-8 w-8 text-blue-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Overdue</p>
                          <p className="text-2xl font-bold text-red-500">{stats?.summary?.overdue_count || 0}</p>
                        </div>
                        <AlertTriangle className="h-8 w-8 text-red-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Cost Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Labor vs Parts</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Labor Cost</span>
                          <span className="font-medium">{formatCurrency(stats?.summary?.total_labor_cost)}</span>
                        </div>
                        <div className="w-full bg-secondary rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full" 
                            style={{ 
                              width: `${stats?.summary?.total_cost ? (stats.summary.total_labor_cost / stats.summary.total_cost * 100) : 0}%` 
                            }}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Parts Cost</span>
                          <span className="font-medium">{formatCurrency(stats?.summary?.total_parts_cost)}</span>
                        </div>
                        <div className="w-full bg-secondary rounded-full h-2">
                          <div 
                            className="bg-emerald-500 h-2 rounded-full" 
                            style={{ 
                              width: `${stats?.summary?.total_cost ? (stats.summary.total_parts_cost / stats.summary.total_cost * 100) : 0}%` 
                            }}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Cost by Maintenance Type</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {stats?.by_type && Object.entries(stats.by_type).length > 0 ? (
                          Object.entries(stats.by_type)
                            .sort((a, b) => b[1].cost - a[1].cost)
                            .map(([type, data]) => {
                              const maxCost = Math.max(...Object.values(stats.by_type).map(d => d.cost));
                              const percentage = maxCost > 0 ? (data.cost / maxCost * 100) : 0;
                              const typeColors = {
                                'Routine': 'bg-blue-500',
                                'Repair': 'bg-red-500',
                                'Inspection': 'bg-yellow-500',
                                'Emergency': 'bg-orange-500',
                                'Preventive': 'bg-emerald-500',
                              };
                              return (
                                <div key={type}>
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium">{type}</span>
                                      <span className="text-xs text-muted-foreground">({data.count})</span>
                                    </div>
                                    <span className="text-sm font-bold">{formatCurrency(data.cost)}</span>
                                  </div>
                                  <div className="w-full bg-secondary rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${typeColors[type] || 'bg-primary'}`}
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })
                        ) : (
                          <p className="text-sm text-muted-foreground text-center py-2">No data yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Overdue Alert */}
                {stats?.overdue_maintenance?.length > 0 && (
                  <Card className="border-red-500/50 bg-red-500/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2 text-red-500">
                        <AlertTriangle className="h-4 w-4" />
                        Overdue Maintenance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {stats.overdue_maintenance.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-red-500/10 rounded">
                            <span className="font-medium">{item.equipment_name || 'Unknown Equipment'}</span>
                            <Badge variant="destructive">{item.days_overdue} days overdue</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* UPCOMING TAB */}
              <TabsContent value="upcoming" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-500" />
                      Scheduled Maintenance
                    </CardTitle>
                    <CardDescription>Upcoming maintenance tasks based on scheduled dates</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {stats?.upcoming_maintenance?.length > 0 ? (
                      <div className="space-y-2">
                        {stats.upcoming_maintenance.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                            <div className="flex items-center gap-3">
                              <Package className="h-5 w-5 text-muted-foreground" />
                              <div>
                                <p className="font-medium">{item.equipment_name || 'Unknown Equipment'}</p>
                                <p className="text-sm text-muted-foreground">Due: {formatDate(item.next_date)}</p>
                              </div>
                            </div>
                            <Badge variant={item.days_until <= 7 ? 'destructive' : item.days_until <= 30 ? 'warning' : 'secondary'}>
                              {item.days_until} days
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No upcoming maintenance scheduled</p>
                        <p className="text-sm">Add maintenance records with next maintenance dates to see them here</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* HISTORY TAB */}
              <TabsContent value="history" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-emerald-500" />
                      Recent Maintenance History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats?.recent_maintenance?.length > 0 ? (
                      <div className="space-y-2">
                        {stats.recent_maintenance.map((log, idx) => (
                          <div key={log.id || idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/30 transition-colors">
                            <div className="flex items-center gap-3">
                              <CheckCircle className="h-5 w-5 text-emerald-500" />
                              <div>
                                <p className="font-medium">{log.equipment_name || 'General Maintenance'}</p>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <span>{formatDate(log.performed_at)}</span>
                                  <span>•</span>
                                  <span>{log.performed_by}</span>
                                  {log.contractor_vendor && (
                                    <>
                                      <span>•</span>
                                      <span>{log.contractor_vendor}</span>
                                    </>
                                  )}
                                </div>
                                {log.notes && (
                                  <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{log.notes}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <Badge variant="outline">{log.maintenance_type}</Badge>
                                {(log.labor_cost || log.parts_cost) && (
                                  <p className="text-sm font-medium mt-1">
                                    {formatCurrency((log.labor_cost || 0) + (log.parts_cost || 0))}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => openEditDialog(log)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Edit3 className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDeleteLog(log.id)}
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Wrench className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No maintenance records yet</p>
                        <Button variant="outline" className="mt-3" onClick={() => setIsAddLogOpen(true)}>
                          <Plus className="h-4 w-4 mr-1" />
                          Add First Record
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* COSTS TAB */}
              <TabsContent value="costs" className="space-y-4 mt-4">
                {/* Cost by Maintenance Type - Detailed */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="h-5 w-5 text-blue-500" />
                      Cost by Maintenance Type
                    </CardTitle>
                    <CardDescription>Breakdown of labor and parts costs per maintenance type</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {stats?.by_type && Object.keys(stats.by_type).length > 0 ? (
                      <div className="space-y-4">
                        {Object.entries(stats.by_type)
                          .sort((a, b) => b[1].cost - a[1].cost)
                          .map(([type, data]) => {
                            const typeColors = {
                              'Routine': { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-700' },
                              'Repair': { bg: 'bg-red-500', light: 'bg-red-100', text: 'text-red-700' },
                              'Inspection': { bg: 'bg-yellow-500', light: 'bg-yellow-100', text: 'text-yellow-700' },
                              'Emergency': { bg: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-700' },
                              'Preventive': { bg: 'bg-emerald-500', light: 'bg-emerald-100', text: 'text-emerald-700' },
                            };
                            const colors = typeColors[type] || { bg: 'bg-gray-500', light: 'bg-gray-100', text: 'text-gray-700' };
                            const laborPct = data.cost > 0 ? (data.labor / data.cost * 100) : 0;
                            
                            return (
                              <div key={type} className="p-4 border rounded-lg">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
                                    <span className="font-semibold text-lg">{type}</span>
                                    <Badge variant="secondary">{data.count} records</Badge>
                                  </div>
                                  <span className="text-xl font-bold">{formatCurrency(data.cost)}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mb-3">
                                  <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded">
                                    <p className="text-xs text-muted-foreground">Labor</p>
                                    <p className="text-lg font-semibold text-blue-600">{formatCurrency(data.labor)}</p>
                                  </div>
                                  <div className="p-2 bg-emerald-50 dark:bg-emerald-950 rounded">
                                    <p className="text-xs text-muted-foreground">Parts</p>
                                    <p className="text-lg font-semibold text-emerald-600">{formatCurrency(data.parts)}</p>
                                  </div>
                                </div>
                                <div className="w-full bg-secondary rounded-full h-3 overflow-hidden">
                                  <div className="h-full flex">
                                    <div 
                                      className="bg-blue-500 h-full" 
                                      style={{ width: `${laborPct}%` }}
                                      title={`Labor: ${laborPct.toFixed(0)}%`}
                                    />
                                    <div 
                                      className="bg-emerald-500 h-full" 
                                      style={{ width: `${100 - laborPct}%` }}
                                      title={`Parts: ${(100 - laborPct).toFixed(0)}%`}
                                    />
                                  </div>
                                </div>
                                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                                  <span>Labor {laborPct.toFixed(0)}%</span>
                                  <span>Parts {(100 - laborPct).toFixed(0)}%</span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <PieChart className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No cost data by type available</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Monthly Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-emerald-500" />
                      Monthly Cost Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {stats?.monthly_breakdown && Object.keys(stats.monthly_breakdown).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(stats.monthly_breakdown)
                          .sort((a, b) => b[0].localeCompare(a[0]))
                          .slice(0, 12)
                          .map(([month, costs]) => (
                            <div key={month} className="p-3 border rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium">
                                  {new Date(month + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                                </span>
                                <span className="text-lg font-bold">{formatCurrency(costs.total)}</span>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span>Labor: {formatCurrency(costs.labor)}</span>
                                <span>Parts: {formatCurrency(costs.parts)}</span>
                                <span>{stats.monthly_counts[month]} records</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No cost data available</p>
                        <p className="text-sm">Add maintenance records with costs to see breakdown</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Maintenance Log Dialog */}
      <Dialog open={isAddLogOpen} onOpenChange={(open) => !open && closeLogDialog()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isEditMode ? <Edit3 className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
              {isEditMode ? 'Edit Maintenance Record' : 'Log Maintenance Record'}
            </DialogTitle>
            <DialogDescription>
              {isEditMode ? 'Update the maintenance record details' : 'Record maintenance work performed on equipment'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Equipment/Asset *</Label>
                {equipment.length > 0 ? (
                  <Select 
                    value={newLog.equipment_id} 
                    onValueChange={(v) => {
                      const selected = equipment.find(e => e.id === v);
                      setNewLog({...newLog, equipment_id: v, equipment_name: selected?.name || ''});
                    }}
                  >
                    <SelectTrigger className="w-full h-10">
                      <SelectValue placeholder="Select equipment from list" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {equipment.map((eq) => (
                        <SelectItem key={eq.id} value={eq.id}>
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-muted-foreground" />
                            <span>{eq.name || 'Unnamed Equipment'}</span>
                            {eq.tag_number && <span className="text-xs text-muted-foreground">({eq.tag_number})</span>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="space-y-2">
                    <Input 
                      value={newLog.equipment_name}
                      onChange={(e) => setNewLog({...newLog, equipment_name: e.target.value, equipment_id: 'manual'})}
                      placeholder="Enter equipment/asset name"
                      className="h-10"
                    />
                    <p className="text-xs text-muted-foreground">
                      No assets found in this tour. Add assets via the Assets panel, or enter name manually.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label>Date Performed *</Label>
                <Input 
                  type="date" 
                  value={newLog.performed_at}
                  onChange={(e) => setNewLog({...newLog, performed_at: e.target.value})}
                />
              </div>

              <div>
                <Label>Maintenance Type</Label>
                <Select 
                  value={newLog.maintenance_type} 
                  onValueChange={(v) => setNewLog({...newLog, maintenance_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Routine">Routine</SelectItem>
                    <SelectItem value="Repair">Repair</SelectItem>
                    <SelectItem value="Inspection">Inspection</SelectItem>
                    <SelectItem value="Emergency">Emergency</SelectItem>
                    <SelectItem value="Preventive">Preventive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Performed By *</Label>
                <Input 
                  value={newLog.performed_by}
                  onChange={(e) => setNewLog({...newLog, performed_by: e.target.value})}
                  placeholder="Name of technician"
                />
              </div>

              <div>
                <Label>Contractor/Vendor</Label>
                <Input 
                  value={newLog.contractor_vendor}
                  onChange={(e) => setNewLog({...newLog, contractor_vendor: e.target.value})}
                  placeholder="Optional"
                />
              </div>

              <div>
                <Label>Labor Cost ($)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={newLog.labor_cost}
                  onChange={(e) => setNewLog({...newLog, labor_cost: e.target.value})}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label>Parts Cost ($)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={newLog.parts_cost}
                  onChange={(e) => setNewLog({...newLog, parts_cost: e.target.value})}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label>Time Spent (minutes)</Label>
                <Input 
                  type="number" 
                  value={newLog.time_spent_minutes}
                  onChange={(e) => setNewLog({...newLog, time_spent_minutes: e.target.value})}
                  placeholder="60"
                />
              </div>

              <div>
                <Label>Next Maintenance Date</Label>
                <Input 
                  type="date" 
                  value={newLog.next_maintenance_date}
                  onChange={(e) => setNewLog({...newLog, next_maintenance_date: e.target.value})}
                />
              </div>

              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea 
                  value={newLog.notes}
                  onChange={(e) => setNewLog({...newLog, notes: e.target.value})}
                  placeholder="Description of work performed"
                  rows={2}
                />
              </div>

              <div className="col-span-2">
                <Label>Issues Found</Label>
                <Textarea 
                  value={newLog.issues_found}
                  onChange={(e) => setNewLog({...newLog, issues_found: e.target.value})}
                  placeholder="Any issues discovered during maintenance"
                  rows={2}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={closeLogDialog}>Cancel</Button>
              <Button 
                onClick={handleAddLog} 
                disabled={(!newLog.equipment_id && !newLog.equipment_name) || !newLog.performed_by}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                {isEditMode ? 'Update Record' : 'Save Record'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MaintenanceDashboard;
